
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Trash2, Eye, Paperclip, CreditCard, Pencil, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SearchableSelect } from '@/components/SearchableSelect';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { LiabilitiesByBankWidget, LIABILITIES_REFRESH_EVENT } from '@/components/LiabilitiesByBankWidget';
import api from '@/lib/api';

const STATUS_COLORS = {
  Draft: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  Pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Approved: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  'Partially Paid': 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  Paid: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Overdue: 'bg-red-500/10 text-red-400 border-red-500/20',
  Cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const RECURRENCE_OPTIONS = ['monthly', 'quarterly', 'annually'];

const BANK_ACCOUNTS = ['Operating Account', 'Vendor', 'Savings'];
const BANK_ACCOUNT_COLORS = {
  'Operating Account': 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  'Vendor': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Savings': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

function fmtMoney(v) { return `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`; }
function isOverdue(p) {
  if (!p.due_date || ['Paid', 'Cancelled'].includes(p.status)) return false;
  return p.due_date < new Date().toISOString().slice(0, 10);
}

export default function AccountsPayableTab() {
  const { user } = useAuth();
  const role = user?.role || 'Employee';
  const isPrivileged = ['Admin', 'Owner', 'Office Manager', 'Administrator'].includes(role);

  const [payables, setPayables] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [bankFilter, setBankFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState(null); // 'overdue' | 'due_week' | 'due_later' | 'recurring' | null
  const [searchParams, setSearchParams] = useSearchParams();

  // Honor ?bank=<name> and ?urgency=<bucket> from deep-links (e.g. liabilities widget click)
  useEffect(() => {
    const b = searchParams.get('bank');
    if (b && (BANK_ACCOUNTS.includes(b) || b === 'Unassigned')) {
      setBankFilter(b);
    } else if (!b) {
      setBankFilter('all');
    }
    const u = searchParams.get('urgency');
    if (u && ['overdue', 'due_week', 'due_later', 'recurring'].includes(u)) {
      setUrgencyFilter(u);
    } else {
      setUrgencyFilter(null);
    }
  }, [searchParams]);

  const updateBankFilter = (val) => {
    setBankFilter(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'all') next.delete('bank');
    else next.set('bank', val);
    // preserve existing params (?tab=payable)
    setSearchParams(next, { replace: true });
  };

  const clearUrgencyFilter = () => {
    setUrgencyFilter(null);
    const next = new URLSearchParams(searchParams);
    next.delete('urgency');
    setSearchParams(next, { replace: true });
  };
  const [sheetOpen, setSheetOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', method: '', note: '' });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newCategory, setNewCategory] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const savedRef = useRef(false);

  const fetchPayables = useCallback(() => {
    api.get('/accounts-payable').then(r => setPayables(r.data)).catch(() => {});
    // Tell any mounted Liabilities widget to refetch - keeps dashboard / overview
    // / AP-banner cards in sync after any CRUD or status change.
    window.dispatchEvent(new Event(LIABILITIES_REFRESH_EVENT));
  }, []);

  useEffect(() => {
    fetchPayables();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/accounts-payable/categories').then(r => setCategories(r.data)).catch(() => {});
  }, [fetchPayables]);

  const openCreate = () => {
    setForm({ vendor_name: '', invoice_number: '', amount: '', due_date: '', category: '', bank_account: '', project_id: '', description: '', is_recurring: false, recurrence_interval: '' });
    setUploadedFile(null);
    setSheetOpen(true);
  };

  const openDetail = async (p) => {
    try {
      const r = await api.get(`/accounts-payable/${p.id}`);
      setSelected(r.data);
      setDetailOpen(true);
      setShowPaymentForm(false);
      setEditing(false);
      setPaymentForm({ amount: '', date: new Date().toISOString().slice(0, 10), method: '', note: '' });
    } catch { toast.error('Error loading record'); }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await api.get(`/accounts-payable/${selected.id}`);
    setSelected(r.data);
  };

  const handleCreate = async () => {
    if (!form.vendor_name) return toast.error('Vendor name required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Valid amount required');
    if (!form.bank_account) return toast.error('Bank Account is required');
    try {
      await api.post('/accounts-payable', {
        ...form,
        amount: parseFloat(form.amount),
        file_url: uploadedFile?.file_url || '',
        file_id: uploadedFile?.file_id || '',
        file_name: uploadedFile?.file_name || '',
        file_size: uploadedFile?.file_size || 0,
        content_type: uploadedFile?.content_type || '',
      });
      toast.success('Payable created');
      savedRef.current = true;
      setSheetOpen(false);
      setUploadedFile(null);
      fetchPayables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/accounts-payable/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      refreshSelected();
      fetchPayables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const startEditing = () => {
    setEditForm({
      vendor_name: selected.vendor_name || '',
      invoice_number: selected.invoice_number || '',
      amount: String(selected.amount || ''),
      due_date: selected.due_date || '',
      category: selected.category || '',
      bank_account: selected.bank_account || '',
      project_id: selected.project_id || '',
      description: selected.description || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editForm.vendor_name) return toast.error('Vendor name required');
    if (!editForm.amount || parseFloat(editForm.amount) <= 0) return toast.error('Valid amount required');
    if (!editForm.bank_account) return toast.error('Bank Account is required');
    try {
      await api.put(`/accounts-payable/${selected.id}`, {
        ...editForm,
        amount: parseFloat(editForm.amount),
      });
      toast.success('Record updated');
      setEditing(false);
      refreshSelected();
      fetchPayables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payable record?')) return;
    try {
      await api.delete(`/accounts-payable/${id}`);
      toast.success('Deleted');
      setDetailOpen(false);
      fetchPayables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) return toast.error('Payment amount required');
    try {
      await api.post(`/accounts-payable/${selected.id}/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
      });
      toast.success('Payment recorded');
      setShowPaymentForm(false);
      setPaymentForm({ amount: '', date: new Date().toISOString().slice(0, 10), method: '', note: '' });
      refreshSelected();
      fetchPayables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await api.post('/accounts-payable/categories', { name: newCategory.trim() });
      setCategories(prev => [...prev, newCategory.trim()].sort());
      setForm(f => ({ ...f, category: newCategory.trim() }));
      setNewCategory('');
      toast.success('Category added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Already exists'); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const weekEnd = (() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();

  const filtered = payables.filter(p => {
    if (statusFilter === 'active' && ['Paid', 'Cancelled'].includes(p.status)) return false;
    if (statusFilter === 'paid' && p.status !== 'Paid') return false;
    if (statusFilter === 'overdue' && !isOverdue(p)) return false;
    if (bankFilter === 'Unassigned') {
      if (p.bank_account) return false;
    } else if (bankFilter !== 'all' && (p.bank_account || '') !== bankFilter) return false;
    // Urgency filter - overlays on top of status/bank. Skip Paid/Cancelled since
    // they're not "real liabilities" any more (except 'recurring' which projects
    // even from paid ones - but we don't have those rows in the table anyway).
    if (urgencyFilter) {
      const isPaidOrCx = ['Paid', 'Cancelled'].includes(p.status);
      if (urgencyFilter === 'recurring') {
        if (!p.is_recurring || p.status === 'Cancelled') return false;
      } else if (isPaidOrCx) {
        return false;
      } else if (urgencyFilter === 'overdue') {
        if (!isOverdue(p)) return false;
      } else if (urgencyFilter === 'due_week') {
        if (!p.due_date || p.due_date < today || p.due_date > weekEnd) return false;
      } else if (urgencyFilter === 'due_later') {
        if (!p.due_date || p.due_date <= weekEnd) return false;
      }
    }
    const q = search.toLowerCase();
    return !q || (p.vendor_name || '').toLowerCase().includes(q) || (p.invoice_number || '').toLowerCase().includes(q) || (p.project_name || '').toLowerCase().includes(q);
  });

  // Per-bank totals for the bank filter chips (only across active payables)
  const bankTotals = BANK_ACCOUNTS.reduce((acc, b) => {
    acc[b] = payables.filter(p => !['Paid', 'Cancelled'].includes(p.status) && (p.bank_account || '') === b)
      .reduce((s, p) => s + (p.balance_due || 0), 0);
    return acc;
  }, {});
  const unassignedCount = payables.filter(p => !['Paid', 'Cancelled'].includes(p.status) && !p.bank_account).length;

  const totalOutstanding = payables.filter(p => !['Paid', 'Cancelled'].includes(p.status)).reduce((s, p) => s + (p.balance_due || 0), 0);
  const overdueCount = payables.filter(p => isOverdue(p)).length;

  return (
    <div className="space-y-4" data-testid="accounts-payable-tab">
      {/* Liabilities by Bank Account - top-of-page situational awareness */}
      <LiabilitiesByBankWidget variant="ap" />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-3">
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Outstanding</p>
            <p className="text-lg font-bold mt-0.5" data-testid="ap-outstanding">{fmtMoney(totalOutstanding)}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-3">
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Active Bills</p>
            <p className="text-lg font-bold mt-0.5">{payables.filter(p => !['Paid', 'Cancelled'].includes(p.status)).length}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-3">
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Overdue</p>
            <p className={`text-lg font-bold mt-0.5 ${overdueCount > 0 ? 'text-red-400' : ''}`}>{overdueCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-3">
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Paid This Month</p>
            <p className="text-lg font-bold mt-0.5">{fmtMoney(payables.filter(p => p.status === 'Paid').reduce((s, p) => s + (p.amount || 0), 0))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search vendor, invoice, project..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="ap-search" />
        </div>
        <div className="flex gap-1">
          {['active', 'overdue', 'paid', 'all'].map(f => (
            <button key={f} onClick={() => setStatusFilter(f)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${statusFilter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary/50'}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground ml-auto" data-testid="create-payable-btn">
          <Plus className="h-4 w-4 mr-1" /> New Payable
        </Button>
      </div>

      {/* Bank Account Filter - only chips with active records shown (Phase 66.1 UX) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="ap-bank-filter">
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Bank Account:</span>
        <button
          onClick={() => updateBankFilter('all')}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${bankFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
          data-testid="ap-bank-filter-all"
        >
          All ({payables.filter(p => !['Paid', 'Cancelled'].includes(p.status)).length})
        </button>
        {BANK_ACCOUNTS.map(b => {
          const count = payables.filter(p => !['Paid', 'Cancelled'].includes(p.status) && (p.bank_account || '') === b).length;
          // Hide chips with zero active records UNLESS the user has the chip
          // currently selected (so they don't lose context when filtering down).
          if (count === 0 && bankFilter !== b) return null;
          return (
            <button
              key={b}
              onClick={() => updateBankFilter(b)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${bankFilter === b ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
              data-testid={`ap-bank-filter-${b.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span>{b}</span>
              <span className="text-xs opacity-80">({count} · {fmtMoney(bankTotals[b])})</span>
            </button>
          );
        })}
        {(unassignedCount > 0 || bankFilter === 'Unassigned') && (
          <button
            onClick={() => updateBankFilter('Unassigned')}
            className={`px-3 py-1 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${bankFilter === 'Unassigned' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-amber-400/80 hover:text-amber-400'}`}
            data-testid="ap-bank-filter-unassigned"
          >
            <span>Unassigned</span>
            <span className="text-xs opacity-80">({unassignedCount})</span>
          </button>
        )}
      </div>

      {/* Urgency Filter Banner (drilled in from the widget) */}
      {urgencyFilter && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-violet-500/30 bg-violet-500/10 px-3 py-1.5" data-testid="ap-urgency-filter-banner">
          <p className="text-xs">
            Filtered by urgency: <span className="font-semibold capitalize">{urgencyFilter.replace('_', ' ')}</span>
            {bankFilter !== 'all' && <> · Bank: <span className="font-semibold">{bankFilter}</span></>}
            <span className="text-muted-foreground"> · {filtered.length} match{filtered.length === 1 ? '' : 'es'}</span>
          </p>
          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={clearUrgencyFilter} data-testid="ap-clear-urgency-filter">
            <X className="h-3 w-3 mr-1" /> Clear urgency
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Vendor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Invoice #</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Balance</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Due Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Category</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Bank</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(p => (
              <TableRow key={p.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(p)} data-testid={`ap-row-${p.id}`}>
                <TableCell className="font-medium text-sm">{p.vendor_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.invoice_number || '-'}</TableCell>
                <TableCell className="text-sm">{fmtMoney(p.amount)}</TableCell>
                <TableCell className={`text-sm ${(p.balance_due || 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{fmtMoney(p.balance_due)}</TableCell>
                <TableCell>
                  <span className={`text-xs ${isOverdue(p) ? 'text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                    {p.due_date || '-'}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">{p.project_name || '-'}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{p.category}</Badge></TableCell>
                <TableCell>
                  {p.bank_account ? (
                    <Badge variant="outline" className={`text-xs ${BANK_ACCOUNT_COLORS[p.bank_account] || ''}`} data-testid={`ap-bank-cell-${p.id}`}>
                      {p.bank_account}
                    </Badge>
                  ) : (
                    <span className="text-xs text-amber-400/80 italic" title="Set a Bank Account when editing">- Unset</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs ${STATUS_COLORS[isOverdue(p) ? 'Overdue' : p.status] || ''}`}>
                    {isOverdue(p) ? 'Overdue' : p.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={e => { e.stopPropagation(); openDetail(p); }}><Eye className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">No payable records found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Sheet */}
      <Sheet open={sheetOpen} onOpenChange={v => { if (!v && !savedRef.current && uploadedFile?.file_id) api.delete(`/upload/${uploadedFile.file_id}`).catch(() => {}); setSheetOpen(v); if (!v) { setUploadedFile(null); savedRef.current = false; } }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">New Payable</SheetTitle>
            <SheetDescription>Track a vendor invoice or bill for payment.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Vendor Name *</Label><Input value={form.vendor_name || ''} onChange={e => setForm({ ...form, vendor_name: e.target.value })} className="bg-background border-border" data-testid="ap-vendor-name" /></div>
            <div><Label className="text-xs text-muted-foreground">Invoice Number</Label><Input value={form.invoice_number || ''} onChange={e => setForm({ ...form, invoice_number: e.target.value })} className="bg-background border-border" data-testid="ap-invoice-number" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Amount *</Label><Input type="number" step="0.01" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} className="bg-background border-border" data-testid="ap-amount" /></div>
              <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border" data-testid="ap-due-date" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Bank Account *</Label>
              <Select value={form.bank_account || ''} onValueChange={v => setForm({ ...form, bank_account: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="ap-bank-account"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {BANK_ACCOUNTS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Bank account the payment will be issued from. Helps visualize cash needed across accounts.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category || ''} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-1 mt-1">
                <Input placeholder="Add custom category..." value={newCategory} onChange={e => setNewCategory(e.target.value)} className="bg-background border-border text-xs h-7" data-testid="ap-new-category" />
                <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={handleAddCategory}>Add</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Project (overhead allocation)</Label>
              <SearchableSelect
                value={form.project_id || ''}
                onValueChange={v => setForm({ ...form, project_id: v })}
                options={[{ value: '', label: '-- None --' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                placeholder="Select project"
                searchPlaceholder="Search projects..."
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <RichTextEditor value={form.description || ''} onChange={v => setForm({ ...form, description: v })} placeholder="Description..." collapsedHeight={80} />
            </div>
            {/* Recurring */}
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_recurring || false} onChange={e => setForm({ ...form, is_recurring: e.target.checked })} className="rounded border-border" data-testid="ap-recurring-check" />
              <Label className="text-xs text-muted-foreground">Recurring bill</Label>
            </div>
            {form.is_recurring && (
              <Select value={form.recurrence_interval || ''} onValueChange={v => setForm({ ...form, recurrence_interval: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Interval" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {RECURRENCE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {/* File Upload */}
            <div>
              <Label className="text-xs text-muted-foreground">Attach Invoice / Receipt</Label>
              {uploadedFile ? (
                <div className="flex items-center gap-2 p-2 border border-emerald-500/20 rounded-md bg-emerald-500/5 mt-1">
                  <Paperclip className="h-4 w-4 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{uploadedFile.file_name}</p>
                    <p className="text-xs text-muted-foreground">{(uploadedFile.file_size / 1024).toFixed(0)} KB</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { api.delete(`/upload/${uploadedFile.file_id}`).catch(() => {}); setUploadedFile(null); }}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ) : (
                <FileUpload onUploadComplete={setUploadedFile} className="mt-1" />
              )}
            </div>
            <Button onClick={handleCreate} className="w-full bg-primary text-primary-foreground mt-2" data-testid="ap-create-submit">Create Payable</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <SheetTitle className="font-heading">{selected.vendor_name}</SheetTitle>
                    <SheetDescription>{selected.invoice_number ? `Invoice: ${selected.invoice_number}` : 'Vendor Payable'}</SheetDescription>
                  </div>
                  {isPrivileged && !['Paid', 'Cancelled'].includes(selected.status) && !editing && (
                    <Button variant="outline" size="sm" className="text-xs gap-1 h-7" onClick={startEditing} data-testid="ap-edit-btn">
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="space-y-4 mt-4">
                {/* Editable form */}
                {editing ? (
                  <div className="space-y-3 p-3 border border-primary/20 rounded-lg bg-primary/5">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-muted-foreground">Vendor Name *</Label>
                        <Input value={editForm.vendor_name} onChange={e => setEditForm({ ...editForm, vendor_name: e.target.value })} className="bg-background border-border" data-testid="edit-vendor" /></div>
                      <div><Label className="text-xs text-muted-foreground">Invoice #</Label>
                        <Input value={editForm.invoice_number} onChange={e => setEditForm({ ...editForm, invoice_number: e.target.value })} className="bg-background border-border" data-testid="edit-invoice-num" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-muted-foreground">Amount *</Label>
                        <Input type="number" step="0.01" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} className="bg-background border-border" data-testid="edit-amount" /></div>
                      <div><Label className="text-xs text-muted-foreground">Due Date</Label>
                        <Input type="date" value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} className="bg-background border-border" data-testid="edit-due-date" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-muted-foreground">Category</Label>
                        <Select value={editForm.category || ''} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                          <SelectTrigger className="bg-background border-border" data-testid="edit-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs text-muted-foreground">Bank Account *</Label>
                        <Select value={editForm.bank_account || ''} onValueChange={v => setEditForm({ ...editForm, bank_account: v })}>
                          <SelectTrigger className="bg-background border-border" data-testid="edit-bank-account"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                          <SelectContent>{BANK_ACCOUNTS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div><Label className="text-xs text-muted-foreground">Project</Label>
                        <SearchableSelect options={projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))} value={editForm.project_id} onChange={v => setEditForm({ ...editForm, project_id: v })} placeholder="Select project..." />
                      </div>
                    </div>
                    <div><Label className="text-xs text-muted-foreground">Description</Label>
                      <RichTextEditor value={editForm.description} onChange={v => setEditForm({ ...editForm, description: v })} placeholder="Description..." collapsedHeight={60} data-testid="edit-description" /></div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEdit} className="bg-primary text-primary-foreground gap-1" data-testid="edit-save"><Check className="h-3 w-3" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="gap-1"><X className="h-3 w-3" /> Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Status + Amount */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground tracking-wider">Total</p>
                        <p className="text-lg font-bold">{fmtMoney(selected.amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground tracking-wider">Paid</p>
                        <p className="text-lg font-bold text-emerald-400">{fmtMoney(selected.amount_paid)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground tracking-wider">Balance</p>
                        <p className={`text-lg font-bold ${(selected.balance_due || 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{fmtMoney(selected.balance_due)}</p>
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><p className="text-xs uppercase text-muted-foreground">Status</p>
                        <Badge variant="outline" className={`text-xs mt-0.5 ${STATUS_COLORS[isOverdue(selected) ? 'Overdue' : selected.status] || ''}`}>
                          {isOverdue(selected) ? 'Overdue' : selected.status}
                        </Badge>
                      </div>
                      <div><p className="text-xs uppercase text-muted-foreground">Due Date</p><p className={`mt-0.5 ${isOverdue(selected) ? 'text-red-400 font-semibold' : ''}`}>{selected.due_date || '-'}</p></div>
                      <div><p className="text-xs uppercase text-muted-foreground">Category</p><p className="mt-0.5">{selected.category}</p></div>
                      <div><p className="text-xs uppercase text-muted-foreground">Bank Account</p>
                        {selected.bank_account ? (
                          <Badge variant="outline" className={`text-xs mt-0.5 ${BANK_ACCOUNT_COLORS[selected.bank_account] || ''}`} data-testid="ap-detail-bank-account">
                            {selected.bank_account}
                          </Badge>
                        ) : (
                          <p className="mt-0.5 text-amber-400/80 italic text-xs">Not set, edit to assign</p>
                        )}
                      </div>
                      <div><p className="text-xs uppercase text-muted-foreground">Project</p><p className="mt-0.5 truncate">{selected.project_name || '-'}</p></div>
                      <div><p className="text-xs uppercase text-muted-foreground">Submitted By</p><p className="mt-0.5">{selected.submitted_by_name || '-'}</p></div>
                      <div><p className="text-xs uppercase text-muted-foreground">Created</p><p className="mt-0.5">{new Date(selected.created_at).toLocaleDateString()}</p></div>
                      {selected.is_recurring && <div><p className="text-xs uppercase text-muted-foreground">Recurring</p><p className="mt-0.5 capitalize">{selected.recurrence_interval || '-'}</p></div>}
                    </div>

                    {selected.description && <div><p className="text-xs uppercase text-muted-foreground mb-1">Description</p><RichTextDisplay value={selected.description} collapsedHeight={60} /></div>}
                  </>
                )}

                {/* Attached File */}
                {selected.file_url && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Attached File</p>
                    <div className="flex items-center gap-2 p-2 border border-border rounded-md hover:bg-secondary/30 cursor-pointer" onClick={() => setPreviewDoc(selected)}>
                      <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-xs truncate flex-1">{selected.file_name || 'View File'}</span>
                      <Eye className="h-3 w-3 text-primary shrink-0" />
                    </div>
                  </div>
                )}

                {/* Status Actions */}
                {isPrivileged && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Actions</p>
                    <div className="flex flex-wrap gap-1">
                      {selected.status === 'Draft' && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateStatus(selected.id, 'Pending')} data-testid="ap-set-pending">Set Pending</Button>}
                      {['Pending', 'Draft'].includes(selected.status) && <Button size="sm" variant="outline" className="text-xs" onClick={() => updateStatus(selected.id, 'Approved')} data-testid="ap-approve">Approve</Button>}
                      {['Approved', 'Partially Paid'].includes(selected.status) && <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => { setShowPaymentForm(true); setPaymentForm(f => ({ ...f, amount: String(selected.balance_due || ''), date: new Date().toISOString().slice(0, 10) })); }} data-testid="ap-record-payment"><CreditCard className="h-3 w-3" /> Record Payment</Button>}
                      {!['Paid', 'Cancelled'].includes(selected.status) && <Button size="sm" variant="outline" className="text-xs text-destructive border-destructive/30" onClick={() => updateStatus(selected.id, 'Cancelled')} data-testid="ap-cancel">Cancel</Button>}
                      <Button size="sm" variant="outline" className="text-xs text-destructive border-destructive/30" onClick={() => handleDelete(selected.id)} data-testid="ap-delete"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                {showPaymentForm && (
                  <Card className="border-border border-dashed bg-transparent shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold">Record Payment</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs text-muted-foreground">Amount *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} className="bg-background border-border" data-testid="payment-amount" /></div>
                        <div><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" value={paymentForm.date} onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} className="bg-background border-border" /></div>
                      </div>
                      <div><Label className="text-xs text-muted-foreground">Method</Label><Input value={paymentForm.method} onChange={e => setPaymentForm({ ...paymentForm, method: e.target.value })} className="bg-background border-border" placeholder="Check, ACH, Wire..." /></div>
                      <div><Label className="text-xs text-muted-foreground">Note</Label><Input value={paymentForm.note} onChange={e => setPaymentForm({ ...paymentForm, note: e.target.value })} className="bg-background border-border" /></div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddPayment} className="bg-primary text-primary-foreground" data-testid="payment-submit">Save Payment</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowPaymentForm(false)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Payment History */}
                {(selected.payments || []).length > 0 && (
                  <div>
                    <p className="text-xs uppercase text-muted-foreground mb-1">Payment History</p>
                    <div className="space-y-1">
                      {selected.payments.map((pay, i) => (
                        <div key={pay.id || i} className="flex items-center justify-between py-1.5 px-2 border border-border rounded-md text-xs">
                          <div>
                            <span className="font-medium text-emerald-400">{fmtMoney(pay.amount)}</span>
                            <span className="text-muted-foreground ml-2">{pay.date}</span>
                            {pay.method && <span className="text-muted-foreground ml-2">({pay.method})</span>}
                          </div>
                          {pay.note && <span className="text-muted-foreground truncate ml-2 max-w-[120px]">{pay.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <DocumentPreviewModal open={!!previewDoc} onClose={() => setPreviewDoc(null)} doc={previewDoc} />
    </div>
  );
}
